import { useContext, useState, useEffect } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { getAdminMagicSecret } from '../../connector/auth';
import { nonNull } from 't-lib-ui/helpers';
import { TextButton, Input, Note, useToast } from 't-lib-ui/widgets';
import { chevronBackIcon, bugIcon, downloadIcon } from '../../components/icons';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import { adminDebugFeed, DebugFeedResult, requestFeed, subscribeWithMode } from '../../connector/api';
import { FetchError } from 't-lib-ui/connector';

const AdminDebugFeedPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [authFailed, setAuthFailed] = useState(0);
  const [url, setUrl] = useState('');
  // When arriving from a feed's admin page we know which feed this is, so the run can replay the
  // scheduled request (stored ETag/Last-Modified, proxy hint, right sync instance) instead of a
  // bare fetch. A bare fetch can succeed for a feed that fails on every real poll.
  const [feedId, setFeedId] = useState<string | undefined>();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DebugFeedResult | null>(null);
  const [showToast, toastProps] = useToast();
  const [loading, setLoading] = useState(false);

  const magic = getAdminMagicSecret() || adminSecret || '';

  useFlowFormGlobal();

  // Pre-populate URL (and, when we came from a feed page, the feed id) from query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preUrl = params.get('url');
    if (preUrl) setUrl(preUrl);
    setFeedId(params.get('feedId') || undefined);
  }, []);

  useEffect(() => {
    registerKeyHandler('adminDebugFeedPage', {
      'Escape': () => { goTo(routerCtx, '/a/admin/feeds'); return true; },
    }, false, 0);
    return () => unregisterKeyHandler('adminDebugFeedPage');
  }, [routerCtx]);

  const handleLoadFeed = async () => {
    if (!url.trim() || loading) return;
    try {
      setLoading(true);
      let normalizedUrl = url.trim();
      if (normalizedUrl && !/^https?:\/\//i.test(normalizedUrl))
        normalizedUrl = `https://${normalizedUrl}`;
      const auth = nonNull(appState.state.authData);
      const res = await requestFeed(auth, normalizedUrl);
      if ('id' in res) {
        goTo(routerCtx, `/a/admin/feed/${res.id}`);
      } else if (res.feedStatus == 'READY') {
        // Auto-subscribe as public (admin override)
        const subResult = await subscribeWithMode(auth, normalizedUrl, 'public');
        goTo(routerCtx, `/a/admin/feed/${subResult.feed.id}`);
      } else {
        showToast({ children: `Feed status: ${res.feedStatus}`, type: 'info' }, 5000);
      }
    } catch (e: any) {
      if (e instanceof FetchError && e.type == 'InvalidAuth') { setAuthFailed(f => f + 1); return; }
      showToast({ children: e?.message || 'Load feed failed', type: 'error' }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDebug = async (replayScheduled = false) => {
    if ((!url.trim() && !replayScheduled) || running) return;
    try {
      setRunning(true);
      setResult(null);
      let normalizedUrl = url.trim();
      if (normalizedUrl && !/^https?:\/\//i.test(normalizedUrl))
        normalizedUrl = `https://${normalizedUrl}`;
      const res = await adminDebugFeed(nonNull(appState.state.authData), normalizedUrl, magic, replayScheduled ? feedId : undefined);
      setResult(res);
    } catch (e: any) {
      if (e instanceof FetchError && e.type == 'InvalidAuth') { setAuthFailed(f => f + 1); return; }
      showToast({ children: e?.message || 'Debug request failed', type: 'error' }, 5000);
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} authFailed={authFailed} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
          <MainLayout
            selectedFeed="admin-debug"
            right={({ onExpand }) => (
              <FlowForm main={true} className='full-margin'>
                <div style={{ maxWidth: 900, margin: '0 auto' }}>
                  <AdminHeader title="Debug RSS Feed" onExpand={onExpand} />

                  <ButtonBar size="small">
                    <TextButton
                      label="Back"
                      icon={chevronBackIcon}
                      onClick={() => goTo(routerCtx, '/a/admin/feeds')}
                      focussable="flow"
                    />
                    <TextButton
                      label="Run Debug"
                      icon={bugIcon}
                      onClick={() => handleDebug()}
                      focussable="flow"
                      type="major"
                      busy={running}
                      enabled={url.trim() ? 'enabled' : 'disabled'}
                    />
                    {feedId && (
                      <TextButton
                        label="Replay Scheduled Fetch"
                        icon={bugIcon}
                        onClick={() => handleDebug(true)}
                        focussable="flow"
                        busy={running}
                      />
                    )}
                    <TextButton
                      label="Load Feed"
                      icon={downloadIcon}
                      onClick={handleLoadFeed}
                      focussable="flow"
                      busy={loading}
                      enabled={result?.success ? 'enabled' : 'disabled'}
                    />
                  </ButtonBar>

                  <Note {...toastProps} />

                  <div style={{ marginTop: 16 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Feed URL</label>
                    <div onKeyDown={(e) => { if (e.key == 'Enter' && url.trim()) handleDebug(); }}>
                      <Input
                        value={url}
                        onInput={setUrl}
                        placeholder="https://example.com/feed.xml"
                        layout="singleCol"
                      />
                    </div>
                    {feedId && (
                      <div style={{ marginTop: 6, fontSize: '0.85rem', color: 'var(--gray-350)' }}>
                        "Replay Scheduled Fetch" re-runs the poll for feed {feedId} on its own sync
                        instance, with the ETag / Last-Modified and proxy currently stored for it —
                        use it when the plain URL reads fine but the feed keeps failing.
                      </div>
                    )}
                  </div>

                  {result && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{
                        padding: '8px 14px',
                        marginBottom: 16,
                        borderRadius: 6,
                        fontWeight: 600,
                        background: result.success ? '#d4edda' : '#f8d7da',
                        color: result.success ? '#155724' : '#721c24',
                      }}>
                        {result.success ? '✓ Feed parsed successfully' : '✗ Feed parsing failed'}
                      </div>

                      {result.request && (
                        <div style={{ marginBottom: 16, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', color: 'var(--gray-350)' }}>
                          Request: {result.request}
                        </div>
                      )}

                      {result.stderr && (
                        <>
                          <h3 style={{ marginBottom: 8 }}>Debug Log (stderr)</h3>
                          <pre style={{
                            background: '#1e1e1e',
                            color: '#d4d4d4',
                            padding: 16,
                            borderRadius: 6,
                            overflowX: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '0.8rem',
                            maxHeight: 600,
                            overflowY: 'auto',
                          }}>
                            {result.stderr}
                          </pre>
                        </>
                      )}

                      {result.result && (
                        <>
                          <h3 style={{ marginTop: 20, marginBottom: 8 }}>Result JSON</h3>
                          <pre style={{
                            background: 'var(--gray-975)',
                            border: '1px solid var(--gray-850)',
                            padding: 16,
                            borderRadius: 6,
                            overflowX: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '0.8rem',
                            maxHeight: 400,
                            overflowY: 'auto',
                          }}>
                            {JSON.stringify(result.result, null, 2)}
                          </pre>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </FlowForm>
            )}
            startWithLeftCollapsed={false}
          />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminDebugFeedPage;
